import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  ReferenceArea,
  Tooltip,
} from "recharts";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import theme, { shadows } from "@/theme";
import { convertPxToRem } from "@/utils";
import {
  themeColors,
  backgroundColors,
  CustomDot,
  CustomActiveDot,
  ReferenceRanges,
  CustomTooltipTracker,
  generateTicks,
  calculateDomain,
} from "../../../HealthChart/components";
import { useChartSelection } from "../../../HealthChart/hooks";
import {
  getChartFocusStyles,
  getChevronPosition,
} from "../../../HealthChart/utils";
import { ChartDataPoint } from "../../../HealthChart/types";

// Type alias for heart rate data points with required heartRate property
export type HeartRateDataPoint = ChartDataPoint &
  Required<Pick<ChartDataPoint, "heartRate">>;

export interface HeartRateChartProps {
  data: HeartRateDataPoint[];
}

// Scoring rules for heart rate
// <50/min = AMBER
// 50 - 60/min = GREEN
// 61 - 90/min = AMBER
// >90/min = RED
export const getHeartRateColor = (value: number): string => {
  if (value < 50) return themeColors.warning; // AMBER
  if (value >= 50 && value <= 60) return themeColors.success; // GREEN
  if (value >= 61 && value <= 90) return themeColors.warning; // AMBER
  return themeColors.error; // RED
};

// -------- Main Chart --------
const HeartRateChart: React.FC<HeartRateChartProps> = ({ data }) => {
  const { t } = useTranslation();

  // Use shared selection hook
  const {
    setHoveredPoint,
    hasAnimated,
    handlePointClick,
    activePoint,
    activeIndex,
  } = useChartSelection(data);

  // Calculate dynamic Y-axis domain based on data
  const allHeartRateValues = data.map((d) => d.heartRate);
  const yDomain = calculateDomain(allHeartRateValues, 30, 120, 10);

  // Generate dynamic ticks
  const yTicks = generateTicks(yDomain, 10, [50, 60, 90]);

  const heartRateChartHeight = 200;
  const heartRateChartWidth = 27;
  const yMargin = { top: 10, bottom: 10 };

  // Reference ranges configuration
  const referenceRanges = [
    {
      label: t("HealthChart.heartRateChart.zone.high"),
      yValue: 120,
      fontWeight: 700,
      color: themeColors.error,
    },
    {
      label: t("HealthChart.heartRateChart.zone.elevated"),
      yValue: 96,
      fontWeight: 700,
      color: themeColors.warning,
    },
    {
      label: t("HealthChart.heartRateChart.zone.normal"),
      yValue: 81,
      fontWeight: 700,
      color: themeColors.success,
    },
    {
      label: t("HealthChart.heartRateChart.zone.low"),
      yValue: 66,
      fontWeight: 700,
      color: themeColors.warning,
    },
  ];

  return (
    <>
      <style>{getChartFocusStyles("heart-rate-chart")}</style>
      <Box
        className="heart-rate-chart"
        sx={{
          width: "100%",
          background: theme.palette.background.paper,
          borderRadius: 2.5,
          padding: 2,
          boxShadow: shadows.light,
          transition: "background-color 0.3s ease",
          outline: "none",
        }}
      >
        {/* Title */}
        <Typography
          variant="h4"
          sx={{
            fontSize: 16,
            paddingBottom: 2,
          }}
        >
          {t("HealthChart.heartRateChart.title")}
        </Typography>

        {/* Chart Container */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: 200,
            outline: "none",
          }}
        >
          <ResponsiveContainer>
            <LineChart
              data={data}
              margin={{ top: 10, right: 55, left: 0, bottom: 10 }}
              onClick={handlePointClick}
            >
              {/* Grid */}
              <CartesianGrid
                stroke={theme.palette.grey[300]}
                strokeDasharray="5 5"
                vertical={false}
                horizontal={true}
              />

              {/* Reference Lines and Zones */}
              {/* Background zones */}
              <ReferenceArea
                y1={90}
                y2={yDomain[1]}
                fill={backgroundColors.errorLight}
                fillOpacity={0.5}
              />
              <ReferenceArea
                y1={60}
                y2={90}
                fill={backgroundColors.warningLight}
                fillOpacity={0.5}
              />
              <ReferenceArea
                y1={50}
                y2={60}
                fill={backgroundColors.successLight}
                fillOpacity={0.5}
              />
              <ReferenceArea
                y1={yDomain[0]}
                y2={50}
                fill={backgroundColors.warningLight}
                fillOpacity={0.5}
              />

              {/* Dashed lines */}
              <ReferenceLine
                y={90}
                stroke={themeColors.error}
                strokeDasharray="3 3"
                strokeWidth={1}
              />
              <ReferenceLine
                y={60}
                stroke={themeColors.warning}
                strokeDasharray="3 3"
                strokeWidth={1}
              />
              <ReferenceLine
                y={50}
                stroke={themeColors.warning}
                strokeDasharray="3 3"
                strokeWidth={1}
              />

              {/* X Axis */}
              <XAxis
                dataKey="date"
                tick={{
                  fontSize: 10,
                  fontWeight: 700,
                  fill: theme.palette.grey[700],
                }}
                axisLine={{ stroke: theme.palette.grey[500] }}
                tickLine={false}
                dy={5}
              />

              {/* Y Axis */}
              <YAxis
                domain={yDomain}
                ticks={yTicks}
                tick={{
                  fontSize: 11,
                  fontWeight: 700,
                  fill: theme.palette.grey[700],
                }}
                axisLine={{ stroke: theme.palette.grey[500] }}
                tickLine={false}
                width={heartRateChartWidth}
              />

              {/* Tooltip with custom cursor for vertical dashed line - placed before Line so it renders behind dots */}
              <Tooltip
                content={(props) => (
                  <CustomTooltipTracker
                    {...props}
                    setHoveredPoint={setHoveredPoint}
                  />
                )}
                cursor={{
                  stroke: theme.palette.grey[900],
                  strokeWidth: 1,
                  strokeDasharray: "4 4",
                }}
              />

              {/* Heart Rate Line */}
              <Line
                type="linear"
                dataKey="heartRate"
                stroke={theme.palette.grey[300]}
                strokeWidth={2}
                dot={(props: any) => (
                  <CustomDot {...props} getColorFn={getHeartRateColor} />
                )}
                activeDot={(props: any) => (
                  <CustomActiveDot {...props} getColorFn={getHeartRateColor} />
                )}
                isAnimationActive={hasAnimated.current}
                animationDuration={800}
                animationEasing="ease-in-out"
              />
            </LineChart>
          </ResponsiveContainer>

          {/* Reference Range Labels */}
          <ReferenceRanges
            yDomain={yDomain}
            chartHeight={heartRateChartHeight}
            yMargin={yMargin}
            ranges={referenceRanges}
          />
        </Box>

        {/* Selected Point Display */}
        <div style={{ position: "relative" }}>
          {/* Chevron indicator */}
          {activeIndex !== -1 && (
            <div
              style={{
                position: "absolute",
                left: getChevronPosition(
                  activeIndex,
                  data.length,
                  heartRateChartWidth
                ),
                top: -8,
                transform: "translateX(-50%)",
                width: 0,
                height: 0,
                borderLeft: "8px solid transparent",
                borderRight: "8px solid transparent",
                borderBottom: `8px solid ${theme.palette.grey[200]}`,
                zIndex: 10,
              }}
            />
          )}

          <Box
            sx={{
              padding: convertPxToRem(16),
              background: theme.palette.grey[200],
              borderRadius: convertPxToRem(10),
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                color: theme.palette.grey[700],
              }}
            >
              <span style={{ paddingRight: 3 }}>
                {t("HealthChart.heartRateChart.datePrefix")}
              </span>
              <span>{activePoint.fullDate}</span>
            </Typography>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span
                style={{
                  fontSize: convertPxToRem(24),
                  fontWeight: 800,
                  color: theme.palette.grey[700],
                }}
              >
                {activePoint.heartRate}
              </span>
              <span
                style={{
                  fontSize: convertPxToRem(14),
                  color: theme.palette.grey[900],
                  marginLeft: convertPxToRem(4),
                }}
              >
                {t("HealthChart.heartRateChart.unit")}
              </span>
            </div>
          </Box>
        </div>
      </Box>
    </>
  );
};

export default HeartRateChart;
